import { Separator } from "@monbits/ui";
import { Copy, ExternalLink } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import PaymentStatusLabel from "src/components/payment-status/payment-status";
import SpinWithMask from "src/components/spin-with-mask/spin-with-mask";
import { StatusBadge } from "src/components/status-badge";
import TimeLabel from "src/components/time-label/time-label";
import type { MerchantBalance, Withdrawal } from "src/types";
import copyToClipboard from "src/utils/copy-to-clipboard";
import { formatCryptoAmount, splitTicker } from "src/utils/format";

interface Props {
	data?: Withdrawal;
	balances: MerchantBalance[];
	openNotificationFunc: (title: string, description: string) => void;
}

const WithdrawalDescCard = ({
	data,
	openNotificationFunc,
	balances,
}: Props) => {
	const [balance, setBalance] = useState<MerchantBalance>();

	const loadBalance = useCallback(() => {
		if (!data) {
			return;
		}
		const foundBalance = balances.find((item) => item.id === data.balanceId);
		setBalance(foundBalance);
	}, [data, balances]);

	useEffect(() => {
		loadBalance();
	}, [loadBalance]);

	if (!data || !balance) {
		return <SpinWithMask isLoading={true} />;
	}

	const { token, blockchain } = splitTicker(balance.ticker);
	const displayTicker = token || blockchain;

	const copyText = (text: string) => {
		copyToClipboard(text, openNotificationFunc);
	};

	const isFailed = data.status === "failed";
	// Hide updatedAt when it's effectively the same as createdAt (no progression)
	const showUpdated = data.updatedAt && data.updatedAt !== data.createdAt;

	return (
		<div className="space-y-8">
			{/* Hero — amount + status + network. The merchant's headline
			    questions: "did the money go out, how much, to which chain?" */}
			<div className="space-y-1.5">
				<div className="flex flex-wrap items-center gap-2">
					<p className="text-xs text-muted-foreground">Withdrawing</p>
					<PaymentStatusLabel status={data.status} />
					{balance.isTest && <StatusBadge type="warning">TESTNET</StatusBadge>}
				</div>
				<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
					<span className="text-3xl font-semibold leading-none tracking-tight tabular-nums font-mono">
						{formatCryptoAmount(data.amount, { maxDecimals: 6 })}
					</span>
					<span className="text-lg font-medium text-muted-foreground">
						{displayTicker}
					</span>
				</div>
				<p className="text-xs text-muted-foreground">
					to{" "}
					<span className="font-medium text-foreground">
						{balance.blockchainName}
					</span>
				</p>
			</div>

			{/* Failure block — surfaces immediately if the withdrawal failed.
			    Uses destructive-tinted tokens so it reads as "something went
			    wrong" rather than another data row. */}
			{(isFailed || data.failureReason) && data.failureReason && (
				<div className="rounded-lg border border-(--error-border) bg-(--error-bg) p-3">
					<p className="mb-1 text-[11px] font-medium uppercase tracking-[0.16em] text-(--error-fg)">
						Failure reason
					</p>
					<p className="text-sm leading-relaxed text-(--error-fg)">
						{data.failureReason}
					</p>
				</div>
			)}

			<Separator />

			{/* Sent to address — same "Send to" pattern as the order address card. */}
			<button
				type="button"
				aria-label="Copy recipient address"
				className="group w-full rounded-lg border border-border bg-muted/60 p-3 text-left transition-colors hover:bg-muted"
				onClick={() => copyText(data.recipientAddress)}
			>
				<div className="space-y-1.5">
					<p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
						Sent to
					</p>
					<div className="flex items-start gap-3">
						<span className="flex-1 text-sm font-mono break-all leading-relaxed">
							{data.recipientAddress}
						</span>
						<Copy className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
					</div>
				</div>
			</button>

			{/* Transaction proof — hash + explorer link, or pending state. */}
			<div className="rounded-lg border border-border bg-muted/40 p-3">
				<p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
					Transaction hash
				</p>
				{data.transactionHash ? (
					<div className="flex items-start gap-3">
						<span className="flex-1 text-sm font-mono break-all leading-relaxed">
							{data.transactionHash}
						</span>
						<div className="flex flex-shrink-0 items-center gap-1">
							<button
								type="button"
								aria-label="Copy transaction hash"
								className="p-1 text-muted-foreground transition-colors hover:text-primary"
								onClick={() => copyText(data.transactionHash || "")}
							>
								<Copy aria-hidden="true" className="h-3.5 w-3.5" />
							</button>
							{data.explorerLink && (
								<button
									type="button"
									aria-label="View on blockchain explorer"
									className="p-1 text-muted-foreground transition-colors hover:text-primary"
									onClick={(e) => {
										e.stopPropagation();
										window.open(data.explorerLink, "_blank");
									}}
								>
									<ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
								</button>
							)}
						</div>
					</div>
				) : (
					<p className="text-sm text-muted-foreground">
						Awaiting blockchain dispatch
					</p>
				)}
			</div>

			{/* Fees breakdown — separate concern from amount. Helps the
			    merchant understand the real cost of this withdrawal. */}
			<div className="rounded-lg border border-border bg-muted/40 p-3">
				<p className="mb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
					Fees
				</p>
				<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
					<dt className="text-muted-foreground">Service fee</dt>
					<dd className="text-right font-mono tabular-nums">
						{formatCryptoAmount(data.serviceFee, { maxDecimals: 6 })}{" "}
						{displayTicker}
					</dd>
					{data.transactionNetworkFee && (
						<>
							<dt className="text-muted-foreground">Network fee</dt>
							<dd className="text-right font-mono tabular-nums">
								{formatCryptoAmount(data.transactionNetworkFee, {
									maxDecimals: 8,
								})}{" "}
								{blockchain}
							</dd>
						</>
					)}
				</dl>
			</div>

			<Separator />

			{/* Metadata — every remaining field surfaced. Withdrawal ID,
			    balance/network, timestamps, blockchain status. */}
			<dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm">
				<dt className="text-muted-foreground">Withdrawal ID</dt>
				<dd className="min-w-0">
					<button
						type="button"
						aria-label="Copy withdrawal ID"
						className="group ml-auto flex items-center gap-2"
						onClick={() => copyText(data.uuid)}
					>
						<span className="truncate font-mono text-xs">{data.uuid}</span>
						<Copy className="h-3 w-3 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
					</button>
				</dd>

				<dt className="text-muted-foreground">Balance</dt>
				<dd className="text-right font-medium">
					{balance.blockchainName} · {displayTicker}
				</dd>

				<dt className="text-muted-foreground">Created</dt>
				<dd className="text-right">
					<TimeLabel time={data.createdAt} />
				</dd>

				{showUpdated && (
					<>
						<dt className="text-muted-foreground">Last updated</dt>
						<dd className="text-right">
							<TimeLabel time={data.updatedAt as string} />
						</dd>
					</>
				)}

				{data.transactionStatus && (
					<>
						<dt className="text-muted-foreground">Blockchain status</dt>
						<dd className="text-right capitalize">{data.transactionStatus}</dd>
					</>
				)}
			</dl>

			{/* Description — labeled card matching the other supporting cards. */}
			{data.description && (
				<div className="rounded-lg border border-border bg-muted/40 p-3">
					<p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
						Description
					</p>
					<p className="text-sm leading-relaxed">{data.description}</p>
				</div>
			)}
		</div>
	);
};

export default WithdrawalDescCard;
